import NiceModal from '@ebay/nice-modal-react';

import { useCallback, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import ConfirmationModal from 'components/global/ConfirmationModal';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { PROFILE } from 'services/consts';

import { setLoading } from 'stores/layoutSlice';

import AppLogger from 'utils/AppLogger';

import OTPModal from './OTPModal';
import * as S from './OTPVerification.styled';

/**
 * @param {object} props;
 * @param {"bankInfo" | "currentPhone" | "ownership"} [props.otpType]
 */
const OTPVerification = ({ name, isVerified, onVerification, type = 'phone', otpType }) => {
	const formMethods = useFormContext();

	if (!formMethods) throw new Error('OTPVerification: missing form context!');

	const { getFieldState, resetField, getValues, watch, setValue, formState } = formMethods;

	const fieldState = getFieldState(name, formState);
	const isInvalid = fieldState?.invalid;
	const isDirty = !isVerified || fieldState?.isDirty;
	const isMail = type === 'email';

	const { t } = useTranslation();
	const { showSuccess, showError } = useAlert();
	const axiosPrivate = useAxiosPrivate();

	const [isLoading, setIsLoading] = useState(false);
	const [disabled, setDisabled] = useState(isVerified);
	const [verifiedOwnership, setVerifiedOwnership] = useState(!isVerified);
	const [loadingVerify, setLoadingVerify] = useState(false);
	const isHandleVerifyOTPPending = useRef(false);

	const token = useRef(null);
	const dispatch = useDispatch();

	const handleSendCode = async () => {
		const url = isMail
			? PROFILE.REQUEST_MAIL_VERIFICATION
			: verifiedOwnership
				? PROFILE.SEND_VERIFY_CODE
				: PROFILE.REQUEST_MOBILE_OWNERSHIP_VERIFICATION;
		const payload = isMail ? { mail: getValues(name) } : { phone: getValues(name) };
		otpType && (payload.otpType = otpType);
		return await axiosPrivate
			.post(url, payload)
			.then((response) => {
				if (response.status === 200) {
					showSuccess({ message: 'mobileNumber.codeSended' });
					if (response.data?.token) {
						token.current = response.data.token;
						return response.data.token;
					} else {
						showError({ message: 'mobileNumber.cantVerified' });
						AppLogger.error('OTPVerification : Token was not come from the server');
						return false;
					}
				} else {
					showError({ message: 'mobileNumber.cantVerified' });
					AppLogger.error(`OTPVerification: Error while sending verify code: ${response}`);
					return false;
				}
			})
			.catch((error) => {
				if (error?.response?.data?.code === 'error.B008') {
					showError({
						header: 'error.B008',
						message: 'errorMsg.telephoneNumberValidation'
					});
				} else if (error?.response?.data?.code === 'error.B009') {
					showError({
						message: t('error.B009', {
							minutes: 2
						})
					});
				} else if (error?.response?.data?.code === 'error.EM02') {
					showError({
						message: 'error.EM02'
					});
				} else if (error?.response?.data?.code === 'error.EM04') {
					showError({
						message: 'error.EM04'
					});
				} else {
					showError();
					AppLogger.error(`OTPVerification: Error while sending verify code: ${error}`);
				}
				return false;
			});
	};

	const handleVerifyOTP = async (modal, code) => {
		if (isHandleVerifyOTPPending.current) {
			return;
		}
		isHandleVerifyOTPPending.current = true;
		setLoadingVerify(true);
		setLoading(true);

		const data = getValues(name);

		const url = isMail
			? PROFILE.VERIFY_MAIL
			: verifiedOwnership
				? PROFILE.VERIFY_MOBILE
				: PROFILE.VERIFY_MOBILE_OWNERSHIP;
		const payload = isMail
			? { mail: data, code, token: token.current }
			: { phone: data, code, token: token.current };
		await axiosPrivate
			.post(url, payload)
			.then((response) => {
				if (response.status === 200) {
					modal.resolve({ verified: true });
					modal.remove();
					if (!isMail) {
						showSuccess({ message: 'mobileNumber.verified' });
					}
				} else {
					AppLogger.error(response);
					showError({ message: isMail ? 'email.cantVerified' : 'mobileNumber.cantVerified' });
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				const errorCode = error?.response?.data?.code;
				if (errorCode === 'error.B011' || errorCode === 'error.B019') {
					showError({
						message: errorCode
					});
				} else {
					showError({ message: isMail ? 'email.cantVerified' : 'mobileNumber.cantVerified' });
				}
			});
		setLoading(false);
		setLoadingVerify(false);
		isHandleVerifyOTPPending.current = false;
	};

	const handleChangeButton = async () => {
		await NiceModal.show(ConfirmationModal, {
			title: 'otp.areYouSureToChange',
			subTitle: !isMail && !verifiedOwnership ? 'otp.youNeedToVerifyYourPhoneOwnership' : '',
			yesAction: async () => {
				if (isMail) {
					setDisabled(false);
				} else {
					dispatch(setLoading(true));

					const token = await handleSendCode();

					dispatch(setLoading(false));

					if (token) {
						await NiceModal.show(OTPModal, {
							data: getValues(name),
							sendOTPCode: handleSendCode,
							verifyOTP: handleVerifyOTP,
							loadingVerify,
							isMail
						}).then((res) => {
							if (res) {
								const { verified = false } = res;
								if (verified) {
									setDisabled(false);
									setVerifiedOwnership(true);
								}
							}
						});
					}
				}
			}
		});
	};

	const handleVerifyButton = async () => {
		setIsLoading(true);

		const token = await handleSendCode();
		if (token) {
			const value = getValues(name);
			await NiceModal.show(OTPModal, {
				data: value,
				sendOTPCode: handleSendCode,
				verifyOTP: handleVerifyOTP,
				loadingVerify,
				isMail
			}).then((res) => {
				if (res) {
					const { verified = false } = res;
					if (verified) {
						setDisabled(true);
						resetField(name, value);
						setVerifiedOwnership(false);
						onVerification && onVerification(value);
					}
				}
			});
		}
		setIsLoading(false);
	};

	const handleCancelButton = useCallback(() => {
		setVerifiedOwnership(false);
		setDisabled(true);
	}, []);

	return (
		<S.OTPContainer>
			<S.OTPStringContainer>
				{isMail ? (
					<StringInput
						name={name}
						successMessage={isVerified && !isDirty && 'otp.emailVerified'}
						errorMessage={isDirty && 'otp.emailNotVerified'}
						disabled={disabled || isLoading}
					/>
				) : (
					<PhoneNumber
						name={name}
						value={watch('phone')}
						onChange={(value) => {
							setValue('phone', value, { shouldValidate: true, shouldDirty: true });
						}}
						successMessage={isVerified && !isDirty && 'otp.phoneVerified'}
						errorMessage={isDirty && 'otp.phoneNotVerified'}
						disabled={disabled || isLoading}
					/>
				)}
			</S.OTPStringContainer>

			{disabled && <Button variant="outline" label="change" onClick={handleChangeButton} />}
			{!disabled && !isDirty && <Button variant="outline" label="cancel" onClick={handleCancelButton} />}
			{!disabled && isDirty && (
				<Button
					variant="blue"
					label="verify"
					disabled={watch(name) === null || isInvalid}
					loading={isLoading}
					onClick={handleVerifyButton}
					disabledTooltip={isMail ? 'errorMsg.emailValidation' : 'errorMsg.telephoneNumberValidation'}
				/>
			)}
		</S.OTPContainer>
	);
};

export default OTPVerification;
